import { useState } from "react";
import { Text, Link, Button, Spinner, Flex, Box } from "@radix-ui/themes";
import { ExternalLinkIcon, ChevronLeftIcon } from "@radix-ui/react-icons";
import { rewriteUrl } from "~/utils/rewrite-news-urls";
import axios from "axios";

interface NewsPayload {
  transmission_id: string;
  uuid: string;
  datetime: string;
  headline: string;
  summary: string;
  symbols: string[];
  metacodes: string[];
}

const MobileNewsModal = ({
  url,
  children,
}: {
  url: string;
  children: React.ReactNode;
}) => {
  const [newsData, setNewsData] = useState<NewsPayload | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const fetchNewsData = async () => {
    if (newsData) return;
    setIsLoading(true);
    setError(null);
    try {
      const apiUrl = `${rewriteUrl(url, true)}${
        url.includes("?") ? "&" : "?"
      }format=json`;

      const response = await axios.get(apiUrl);

      if (!response.data) {
        throw new Error(`Error fetching news: ${response.statusText}`);
      }
      setNewsData(response.data);
    } catch (err) {
      console.error("Error fetching news data:", err);
      setError(err instanceof Error ? err.message : "Failed to load news");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    fetchNewsData();
  };

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpen();
          }
        }}
        style={{ cursor: "pointer" }}
      >
        {children}
      </span>

      {isOpen && (
        <Box
          style={{
            position: "absolute",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            backgroundColor: "var(--color-background)",
            zIndex: 50,
            borderRadius: "2rem",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile Header */}
          <Flex
            align="center"
            justify="between"
            style={{
              padding: "16px",
              borderBottom: "1px solid var(--gray-4)",
              backgroundColor: "var(--gray-1)",
              minHeight: "56px",
            }}
          >
            <Button
              variant="ghost"
              size="2"
              onClick={() => setIsOpen(false)}
              style={{
                padding: "8px",
                borderRadius: "50%",
              }}
            >
              <ChevronLeftIcon width="18" height="18" />
            </Button>
            <Text size="3" weight="bold">
              News
            </Text>
            <Box style={{ width: "40px" }} />
          </Flex>

          {/* Modal Content */}
          <Box
            style={{
              flex: 1,
              padding: "16px",
              overflowY: "auto",
            }}
          >
            {isLoading && (
              <Flex justify="center" align="center" style={{ padding: "2rem" }}>
                <Spinner size="2" />
              </Flex>
            )}

            {error && (
              <Text as="p" color="red" size="2">
                {error}
              </Text>
            )}

            {!isLoading && newsData && (
              <Flex direction="column" gap="4">
                <Text
                  as="div"
                  size="4"
                  weight="bold"
                  style={{ lineHeight: "1.3" }}
                >
                  {newsData.headline}
                </Text>

                <Text as="div" size="2" color="gray">
                  {new Date(newsData.datetime).toLocaleString()}
                </Text>

                <Text as="div" size="2" style={{ lineHeight: "1.6" }}>
                  {newsData.summary}
                </Text>

                <Button
                  size="3"
                  variant="solid"
                  asChild
                  style={{ marginTop: "16px" }}
                >
                  <Link
                    href={rewriteUrl(url)}
                    className="no-underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Flex align="center" gap="2">
                      <span>Open Full Article</span>
                      <ExternalLinkIcon width="16" height="16" />
                    </Flex>
                  </Link>
                </Button>
              </Flex>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default MobileNewsModal;
